import {
  Box3,
  Frustum,
  Vector3,
  Vector2,
  Matrix4,
  Math,
} from 'three';
import random from 'lodash/random';
import Scene from '@/scripts/stage/Scene';
import Camera from '@/scripts/stage/Camera';
import loop from '@/scripts/loop';
import { TimelineMax, Elastic } from 'gsap';
import { isTouchDevice } from '@/scripts/utils';
import EventHub from '@/scripts/eventHub';
import { Power4 } from 'gsap/EasePack';


export default class ModelGroup {
  constructor(id) {
    this.id = id;
    this.model = null;
    this.clones = [];
    this.timeScale = 0.0005;
    this.checking = false;
    this.frustum = new Frustum();
    this.cameraViewProjectionMatrix = new Matrix4();
    this.clonesVisible = true;
    this.frustumNeedUpdate = false;
    this.renderHandler = time => this.render(time);
    this.clonesAreExit = false;
    this.clonesAreFlowing = false;

    this.resize();
    EventHub.$on('windowResize', () => { this.resize(); });
  }

  createClones() {
    if (isTouchDevice()) return;
    const clonesNumber = 10;
    for (let index = 0; index < clonesNumber; index += 1) {
      const clone = this.model.clone();
      this.setClonePosition(clone, index);
      Scene.add(clone);
      this.clones.push(clone);
    }
  }

  setClonePosition(clone, i) {
    const firstLevel = -5;
    const secondLevel = -10;
    const thirdLevel = -12;

    switch (true) {
      case i === 0:
        clone.userData.position = new Vector3(-9, 7, firstLevel);
        clone.userData.startPosition = new Vector3(-12, 30, firstLevel);
        clone.userData.scale = new Vector3(0.6, 0.6, 0.6);
        clone.userData.startScale = new Vector3(3.6, 3.6, 3.6);
        break;

      case i === 1:
        clone.userData.position = new Vector3(9, -7, firstLevel);
        clone.userData.startPosition = new Vector3(12, -30, firstLevel);
        clone.userData.scale = new Vector3(0.6, 0.6, 0.6);
        clone.userData.startScale = new Vector3(3.6, 3.6, 3.6);
        break;

      case i === 2:
        clone.userData.position = new Vector3(21, 7, firstLevel);
        clone.userData.startPosition = new Vector3(24, 30, firstLevel);
        clone.userData.scale = new Vector3(0.6, 0.6, 0.6);
        clone.userData.startScale = new Vector3(3.6, 3.6, 3.6);
        break;

      case i === 3:
        clone.userData.position = new Vector3(33, -7, firstLevel);
        clone.userData.startPosition = new Vector3(33, -30, firstLevel);
        clone.userData.scale = new Vector3(0.6, 0.6, 0.6);
        clone.userData.startScale = new Vector3(3.6, 3.6, 3.6);
        break;

        // Second level

      case i === 4:
        clone.userData.position = new Vector3(9, 7, secondLevel);
        clone.userData.startPosition = new Vector3(12, 35, secondLevel);
        clone.userData.scale = new Vector3(0.5, 0.5, 0.5);
        clone.userData.startScale = new Vector3(3.0, 3.0, 3.0);
        break;

      case i === 5:
        clone.userData.position = new Vector3(-9, -7, secondLevel);
        clone.userData.startPosition = new Vector3(-12, -35, secondLevel);
        clone.userData.scale = new Vector3(0.5, 0.5, 0.5);
        clone.userData.startScale = new Vector3(3.0, 3.0, 3.0);
        break;

      case i === 6:
        clone.userData.position = new Vector3(21, -7, secondLevel);
        clone.userData.startPosition = new Vector3(24, -30, secondLevel);
        clone.userData.scale = new Vector3(0.5, 0.5, 0.5);
        clone.userData.startScale = new Vector3(3.0, 3.0, 3.0);
        break;

      case i === 7:
        clone.userData.position = new Vector3(33, 7, secondLevel);
        clone.userData.startPosition = new Vector3(33, 30, secondLevel);
        clone.userData.scale = new Vector3(0.5, 0.5, 0.5);
        clone.userData.startScale = new Vector3(3.0, 3.0, 3.0);
        break;

        // Third level

      case i === 8:
        clone.userData.position = new Vector3(-3, 2, thirdLevel);
        clone.userData.startPosition = new Vector3(-6, 40, thirdLevel);
        clone.userData.scale = new Vector3(0.3, 0.3, 0.3);
        clone.userData.startScale = new Vector3(2.2, 2.2, 2.2);
        break;

      case i === 9:
        clone.userData.position = new Vector3(30, -2, thirdLevel);
        clone.userData.startPosition = new Vector3(30, -40, thirdLevel);
        clone.userData.scale = new Vector3(0.3, 0.3, 0.3);
        clone.userData.startScale = new Vector3(2.2, 2.2, 2.2);
        break;

      default:
        break;
    }

    clone.userData.rotation = new Vector3(
      Math.degToRad(random(-20, 20)),
      Math.degToRad(random(-20, 20)),
      0,
    );
    clone.userData.startRotation = new Vector3(0, Math.degToRad(180), 0);
    clone.userData.inertia = new Vector2(0, 0);
    clone.userData.fresh = true;
    this.initClone(clone);
  }

  initClone(clone) {
    clone.position.copy(clone.userData.position);
    clone.userData.positionShift = new Vector3(0, 0, 0);
    clone.userData.positionReference = new Vector3(0, 0, 0);
    clone.userData.positionReference.copy(clone.userData.position);
  }

  startFlow() {
    if (!this.clonesAreFlowing) {
      this.clonesAreFlowing = true;
      loop.add(this.renderHandler, 'infinite_flow');
    }
  }

  stopFlow() {
    if (this.clonesAreFlowing) {
      this.clonesAreFlowing = false;
      loop.remove('infinite_flow');
    }
  }

  render(time) {
    this.clones.forEach((clone) => {
      let framePositionX = clone.userData.positionReference.x;
      framePositionX -= time * this.timeScale;
      framePositionX -= clone.userData.inertia.x;
      clone.userData.positionReference.x = framePositionX;
      clone.position.x = framePositionX - clone.userData.positionShift.x;

      let framePositionY = clone.userData.positionReference.y;
      framePositionY -= clone.userData.inertia.y;
      clone.userData.positionReference.y = framePositionY;
      clone.position.y = framePositionY + clone.userData.positionShift.y;

      clone.rotation.y -= clone.userData.inertia.x;
      clone.rotation.x += clone.userData.inertia.y;

      if (!this.checking) return;
      const cloneBox = new Box3().setFromObject(clone);
      clone.isInViewport = this.frustum.intersectsBox(cloneBox);
      if (!clone.userData.fresh) {
        if (!clone.isInViewport) {
          clone.userData.positionReference.x = 36;
          clone.userData.positionReference.y = clone.userData.position.y;
          clone.userData.fresh = true;
        }
      } else if (clone.isInViewport) {
        clone.userData.fresh = false;
      }
    });
    if (this.frustumNeedUpdate) this.updateFrustum();
  }

  resize() {
    this.frustumNeedUpdate = true;
  }

  updateFrustum() {
    const camera = Camera.clone();
    camera.updateMatrixWorld();
    camera.matrixWorld.getInverse(camera.matrixWorld);
    this.cameraViewProjectionMatrix.multiplyMatrices(
      camera.projectionMatrix,
      camera.matrixWorldInverse,
    );
    this.frustum.setFromMatrix(this.cameraViewProjectionMatrix);
  }

  enterClones(mode) {
    if (typeof mode === 'undefined') mode = 'general';
    this.clonesAreExit = false;

    const tl = new TimelineMax({ paused: true });
    tl.addLabel('start');
    this.clones.forEach((clone, index) => {
      switch (mode) {
        case 'general':
          clone.userData.positionShift.subVectors(
            clone.userData.startPosition,
            clone.userData.position,
          );
          clone.scale.copy(clone.userData.startScale);
          // Rotation it's Euler not Vector3
          clone.rotation.x = clone.userData.startRotation.x;
          clone.rotation.y = clone.userData.startRotation.y;
          clone.rotation.z = clone.userData.startRotation.z;
          break;

        case 'right':
          clone.userData.positionShift.set(15, 30, 0);
          break;

        case 'left':
          clone.userData.positionShift.set(-15, -30, 0);
          break;

        default:
          break;
      }

      tl.addLabel(`l-${index}`, `start+=${0.02 * index}`);
      tl.to(clone.userData.positionShift, 3, {
        x: 0,
        y: 0,
        ease: Elastic.easeOut.config(0.3, 0.4),
      }, `l-${index}`);
      tl.to(clone.position, 3, {
        z: clone.userData.position.z,
        ease: Elastic.easeOut.config(0.3, 0.4),
      }, `l-${index}`);
      tl.to(clone.rotation, 3, {
        x: clone.userData.rotation.x,
        y: clone.userData.rotation.y,
        z: clone.userData.rotation.z,
        ease: Elastic.easeOut.config(0.3, 0.4),
      }, `l-${index}`);
      tl.to(clone.scale, 3, {
        x: clone.userData.scale.x,
        y: clone.userData.scale.y,
        z: clone.userData.scale.z,
        ease: Elastic.easeOut.config(0.3, 0.4),
      }, `l-${index}`);
    });
    this.startFlow();
    tl.eventCallback('onComplete', () => { this.checking = true; });
    tl.play();
    return tl;
  }

  exitClones(mode) {
    if (typeof mode === 'undefined') mode = 'general';

    this.checking = false;
    this.clonesAreExit = true;

    const tl = new TimelineMax({ paused: true });
    tl.addLabel('start');
    this.clones.forEach((clone, index) => {
      const outDifference = new Vector3();

      switch (mode) {
        case 'general':
          outDifference.subVectors(clone.userData.startPosition, clone.position);
          break;

        case 'right':
          outDifference.set(15, clone.position.y + 30);
          break;

        case 'left':
          outDifference.set(-15, clone.position.y - 30);
          break;

        default:
          break;
      }

      tl.addLabel(`l-${index}`, `start+=${0.02 * index}`);
      tl.to(clone.userData.positionShift, 2.5, {
        x: outDifference.x,
        y: outDifference.y,
        ease: Power4.easeInOut,
      }, `l-${index}`);
    });
    if (mode !== 'general') {
      tl.eventCallback('onComplete', () => { this.destroyClones(); });
    }
    tl.play();
    return tl;
  }

  hideClones() {
    if (!this.clonesVisible) return;
    this.clones.forEach((clone) => {
      Scene.remove(clone);
    });
    this.clonesVisible = false;
  }

  showClones() {
    if (this.clonesVisible) return;
    console.log('show');
    this.clones.forEach((clone) => {
      Scene.add(clone);
    });
    this.clonesVisible = true;
  }

  destroyClones() {
    this.clones.forEach((clone) => {
      Scene.remove(clone);
    });
    this.clones = [];
  }
}
